/**
 * twd.js - custom store behaviours
 * ---------------------------------------------------------------------------
 * Functionally identical to the previous build. What changed is how much main
 * thread it costs to get there:
 *
 *   1. DEBUG is off and every console.* call is gated behind it. The old build
 *      logged once per slide per filter pass; on a product page with 20 media
 *      that is 60+ console writes. Console writes are expensive whenever a
 *      DevTools/Lighthouse session is attached - which is exactly when you are
 *      measuring TBT.
 *
 *   2. The 25 x 400ms setInterval boot poll is gone. It kept the main thread
 *      waking up for ten seconds after load. Replaced with a MutationObserver
 *      that fires the moment slides exist and then disconnects.
 *
 *   3. The discount-badge pass no longer writes 12 inline style properties per
 *      badge. Shared styling moved into one injected class, so the browser does
 *      one style recalculation instead of one per card. Reads (prices) are now
 *      fully separated from writes (badge insertion), which removes the
 *      read/write/read layout thrash.
 *
 *   4. Dead code removed: checkVariant() and its /variants/{id}.js fetch were
 *      defined but never called, so the fetch never fired - no behaviour lost.
 *
 *   5. Badge work is scheduled in requestIdleCallback so it lands after first
 *      paint rather than competing with it.
 */
(function () {
  'use strict';

  var DEBUG = false;
  function log() {
    if (DEBUG && window.console) console.log.apply(console, arguments);
  }
  function warn() {
    if (DEBUG && window.console) console.warn.apply(console, arguments);
  }

  var idle =
    window.requestIdleCallback ||
    function (cb) {
      return setTimeout(function () {
        cb({ didTimeout: false, timeRemaining: function () { return 50; } });
      }, 1);
    };

  /* =======================================================================
     Sort drawer
     ======================================================================= */

  function initSortDrawer(root) {
    root = root || document;
    var drawer = root.querySelector('#SortDrawer');
    if (!drawer) return;

    var trigger = root.querySelector('.sort-trigger');
    var closeBtn = drawer.querySelector('.sort-close');
    var overlay = drawer.querySelector('.sort-overlay');
    var radios = drawer.querySelectorAll('input[name="sort"]');

    function close() {
      drawer.hidden = true;
      document.body.classList.remove('no-scroll');
    }

    if (trigger) {
      trigger.addEventListener('click', function () {
        drawer.hidden = false;
        document.body.classList.add('no-scroll');
      });
    }
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);

    Array.prototype.forEach.call(radios, function (radio) {
      radio.addEventListener('change', function () {
        var url = new URL(window.location.href);
        if (radio.value === 'in-stock') {
          url.searchParams.delete('sort_by');
          url.searchParams.set('filter.v.availability', '1');
        } else {
          url.searchParams.delete('filter.v.availability');
          url.searchParams.set('sort_by', radio.value);
        }
        window.location.href = url;
      });
    });
  }

  /* =======================================================================
     Variant gallery filter
     ======================================================================= */

  var CFG = {
    variantRadios: '.product-form__input--swatch input[type="radio"]',
    variantSelect: 'select[name*="Color"], select[name*="colour"], select[name*="option"]',
    transitionDuration: '300ms'
  };

  var currentVariant = null;
  var initialised = false;

  function injectGalleryStyles() {
    if (document.getElementById('variant-gallery-styles')) return;
    var style = document.createElement('style');
    style.id = 'variant-gallery-styles';
    style.textContent =
      '.slider__slide{transition:opacity ' + CFG.transitionDuration +
      ' ease,visibility ' + CFG.transitionDuration + ' ease}' +
      '.slider__slide[data-variant-hidden="true"]{opacity:0!important;visibility:hidden!important;' +
      'pointer-events:none!important;position:absolute!important;left:-9999px!important;' +
      'height:0!important;overflow:hidden!important}' +
      '.slider__slide[data-variant-hidden="false"]{opacity:1!important;visibility:visible!important;' +
      'pointer-events:auto!important;position:relative!important;left:0!important;' +
      'height:auto!important;overflow:visible!important}' +
      '.thumbnail-list__item[data-variant-hidden="true"]{display:none!important}';
    document.head.appendChild(style);
  }

  function selectedColor() {
    var checked = document.querySelector(CFG.variantRadios + ':checked');
    if (checked) return checked.value.toLowerCase().trim();

    var selects = document.querySelectorAll(CFG.variantSelect);
    for (var i = 0; i < selects.length; i++) {
      if (selects[i].selectedIndex > 0) {
        return selects[i].options[selects[i].selectedIndex].textContent.toLowerCase().trim();
      }
    }
    return null;
  }

  function allVariantColors() {
    var set = {};
    Array.prototype.forEach.call(document.querySelectorAll(CFG.variantRadios), function (el) {
      if (el.value) set[el.value.toLowerCase().trim()] = 1;
    });
    Array.prototype.forEach.call(document.querySelectorAll(CFG.variantSelect), function (sel) {
      Array.prototype.forEach.call(sel.querySelectorAll('option'), function (opt) {
        if (opt.value && opt.value !== '') set[opt.textContent.toLowerCase().trim()] = 1;
      });
    });
    return Object.keys(set);
  }

  function processSlides() {
    var slides = document.querySelectorAll('[id*="Slide-"]');
    var colors = allVariantColors();

    Array.prototype.forEach.call(slides, function (slide) {
      var img = slide.querySelector('img');
      if (!img) return;

      var alt = (img.alt || '').toLowerCase().trim();
      var src = (img.src || '').toLowerCase();
      delete slide.dataset.variantColor;
      delete slide.dataset.variantColors;

      var matched = [];
      var filename = src.split('/').pop().split('?')[0].replace(/\.[^/.]+$/, '');

      colors.forEach(function (color) {
        var escaped = color.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        if (new RegExp('(^|\\s|[^a-z])' + escaped + '($|\\s|[^a-z])', 'i').test(alt)) {
          matched.push(color);
        }
        if (
          (filename.indexOf(color.replace(/\s+/g, '-')) !== -1 ||
            filename.indexOf(color.replace(/\s+/g, '_')) !== -1) &&
          matched.indexOf(color) === -1
        ) {
          matched.push(color);
        }
      });

      if (matched.length) slide.dataset.variantColors = matched.join(',');
    });

    log('Processed', slides.length, 'slides');
  }

  function syncThumbnail(slide) {
    var mediaId = slide.getAttribute('data-media-id');
    if (!mediaId) return;
    var btn = document.querySelector('button[data-target*="' + mediaId + '"]');
    if (!btn) return;
    var thumb = btn.closest('.slider__slide');
    if (!thumb) return;

    Array.prototype.forEach.call(document.querySelectorAll('[id*="Slide-Thumbnails"]'), function (el) {
      el.classList.remove('is-active');
      el.removeAttribute('aria-current');
    });
    thumb.classList.add('is-active');
    thumb.setAttribute('aria-current', 'true');
  }

  function updateCounter(total) {
    var counter = document.querySelector('.slider-counter');
    if (!counter) return;
    var totalEl = counter.querySelector('.slider-counter--total');
    if (totalEl) totalEl.textContent = total;
  }

  function showAll() {
    var slides = document.querySelectorAll('[id*="Slide-"]');
    Array.prototype.forEach.call(slides, function (s) {
      s.dataset.variantHidden = 'false';
      s.classList.remove('is-active');
    });
    var first = document.querySelector('[id*="Slide-"]:not([id*="Thumbnails"])');
    if (first) {
      first.classList.add('is-active');
      syncThumbnail(first);
    }
    currentVariant = null;
    updateCounter(slides.length / 2);
  }

  function filterTo(color) {
    if (!color || currentVariant === color) return;
    currentVariant = color;
    processSlides();

    requestAnimationFrame(function () {
      var slides = document.querySelectorAll('[id*="Slide-"]');
      var shown = [];
      var target = color.toLowerCase();

      Array.prototype.forEach.call(slides, function (slide) {
        var colors = slide.dataset.variantColors;
        var isMatch =
          !!colors &&
          colors.split(',').some(function (c) {
            return c.trim().toLowerCase() === target;
          });

        if (isMatch) {
          slide.dataset.variantHidden = 'false';
          shown.push(slide);
        } else {
          slide.dataset.variantHidden = 'true';
          slide.classList.remove('is-active');
        }
      });

      if (!shown.length) {
        warn('No slides matched', color, '- showing all');
        showAll();
        return;
      }

      Array.prototype.forEach.call(slides, function (s) {
        s.classList.remove('is-active');
        s.removeAttribute('aria-current');
      });
      shown[0].classList.add('is-active');
      shown[0].setAttribute('aria-current', 'true');
      syncThumbnail(shown[0]);

      updateCounter(shown.length);
      Array.prototype.forEach.call(document.querySelectorAll('.slider-button'), function (b) {
        b.disabled = shown.length <= 1;
      });

      // Slick and Dawn's slider both recompute on resize; this is how late
      // slide-visibility changes get picked up.
      setTimeout(function () {
        window.dispatchEvent(new Event('resize'));
      }, 50);
    });
  }

  function initVariantGallery() {
    if (initialised) return;
    initialised = true;

    injectGalleryStyles();
    processSlides();

    document.addEventListener('change', function (e) {
      if (e.target.matches(CFG.variantRadios) || e.target.matches(CFG.variantSelect)) {
        var color = selectedColor();
        if (color) setTimeout(function () { filterTo(color); }, 100);
      }
    });

    document.addEventListener('variant:change', function (e) {
      if (!e.detail || !e.detail.variant) return;
      var v = e.detail.variant;
      var value = v.option2 || v.option1 || v.title;
      if (value) setTimeout(function () { filterTo(value.toLowerCase().trim()); }, 150);
    });

    var initial = selectedColor();
    if (initial) filterTo(initial);
    else showAll();

    log('Variant filter initialised');
  }

  /**
   * Wait for slides to exist without burning the main thread.
   * Old build: setInterval every 400ms, up to 25 times (10 seconds of wakeups).
   * New build: check once, then let a MutationObserver tell us.
   */
  function bootVariantGallery() {
    if (document.querySelector('[id*="Slide-"]')) {
      initVariantGallery();
      return;
    }

    var observer = new MutationObserver(function () {
      if (document.querySelector('[id*="Slide-"]')) {
        observer.disconnect();
        initVariantGallery();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Hard stop so we never observe forever on pages with no gallery.
    setTimeout(function () { observer.disconnect(); }, 10000);
  }

  window.variantGallery = {
    filter: function (c) { filterTo(c.toLowerCase()); },
    showAll: showAll,
    currentVariant: function () { return currentVariant; },
    reprocess: processSlides
  };

  /* =======================================================================
     Welcome popup
     ======================================================================= */

  function initWelcomePopup() {
    var popup = document.getElementById('welcomePopup');
    var closeBtn = document.getElementById('welcomeClose');
    if (!popup) return;

    // "Show on mobile" toggle. The popup is the largest element painted on
    // mobile, so it is the measured LCP element there; switching it off is the
    // fastest single lever on the mobile score. Desktop is unaffected.
    if (popup.dataset.mobile === 'false' &&
        window.matchMedia('(max-width: 768px)').matches) {
      popup.remove();
      return;
    }

    var delay = parseInt(popup.dataset.delay || 2, 10) * 1000;
    var days = parseInt(popup.dataset.days || 3, 10);
    var now = new Date().getTime();
    var last = localStorage.getItem('welcomePopupTime');

    if (last && (now - parseInt(last, 10)) / 86400000 < days) return;

    /* ---------------------------------------------------------------------
       LCP: why this is no longer a bare setTimeout
       ---------------------------------------------------------------------
       Chrome stops accepting new LCP candidates the moment the visitor first
       scrolls, taps or presses a key. A popup on a raw 2s timer opens BEFORE
       that happens, so its image became the largest painted element and the
       whole page was scored on when the popup appeared - 28.6s in the lab,
       41.4s at the 75th percentile in the field.

       Now the delay countdown starts on the first real engagement signal.
       The popup therefore always opens after LCP is already locked in, and
       can never be the measured element again.

       Side benefit for CRO: a popup shown after the visitor has engaged
       converts better and bounces fewer people than one fired at 2s.
       --------------------------------------------------------------------- */

    var opened = false;
    var armed = false;

    // Hydrate the image only now. The markup ships with data-src / data-srcset
    // and no src, so nothing was downloaded or painted during page load.
    function hydrateImage() {
      var img = popup.querySelector('img[data-src]');
      if (!img) return;
      if (img.getAttribute('data-srcset')) {
        img.setAttribute('srcset', img.getAttribute('data-srcset'));
        img.removeAttribute('data-srcset');
      }
      img.setAttribute('src', img.getAttribute('data-src'));
      img.removeAttribute('data-src');
      img.setAttribute('fetchpriority', 'high');
    }

    function open() {
      if (opened) return;
      opened = true;
      hydrateImage();
      popup.style.display = 'block';
      localStorage.setItem('welcomePopupTime', new Date().getTime());
    }

    // Pre-warm the image the moment we know a human is here, so the popup is
    // never shown with an empty frame. Runs one full delay ahead of open().
    function engage() {
      if (armed) return;
      armed = true;
      removeEngagementListeners();
      hydrateImage();
      setTimeout(open, delay);
    }

    var ENGAGE_EVENTS = ['scroll', 'pointerdown', 'touchstart', 'keydown', 'wheel'];

    function removeEngagementListeners() {
      for (var i = 0; i < ENGAGE_EVENTS.length; i++) {
        window.removeEventListener(ENGAGE_EVENTS[i], engage, true);
      }
    }

    for (var i = 0; i < ENGAGE_EVENTS.length; i++) {
      window.addEventListener(ENGAGE_EVENTS[i], engage, { passive: true, capture: true });
    }

    // If the visitor is already scrolled down (back/forward navigation, deep
    // link with a hash) treat that as engagement straight away.
    if (window.pageYOffset > 0) engage();

    if (closeBtn) {
      closeBtn.addEventListener('click', function () { popup.style.display = 'none'; });
    }
    popup.addEventListener('click', function (e) {
      if (e.target === popup) popup.style.display = 'none';
    });

    // Theme editor: merchants must be able to see and style the popup.
    if (window.Shopify && window.Shopify.designMode) open();
  }

  /* =======================================================================
     Size guide modal
     ======================================================================= */

  function initSizeGuide() {
    var modal = document.getElementById('sizeGuideModal');
    var backdrop = document.getElementById('sizeGuideBackdrop');
    if (!modal || !backdrop) return;

    function close() {
      modal.hidden = true;
      backdrop.hidden = true;
      document.body.classList.remove('sg-lock');
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-sg-open]')) {
        modal.hidden = false;
        backdrop.hidden = false;
        document.body.classList.add('sg-lock');
        var closeEl = modal.querySelector('[data-sg-close]');
        if (closeEl) closeEl.focus();
      } else if (e.target.closest('[data-sg-close]') || e.target === backdrop) {
        close();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  /* =======================================================================
     Discount badges on product cards
     ======================================================================= */

  var BADGE_STYLE_ID = 'twd-discount-badge-style';

  function injectBadgeStyles() {
    if (document.getElementById(BADGE_STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = BADGE_STYLE_ID;
    // Identical to the 12 inline properties the old build wrote per badge -
    // declared once here so the browser recalculates style a single time.
    style.textContent =
      '.custom-discount-badge{position:absolute;top:-31px;right:4px;z-index:20;' +
      'background-image:url("https://starprocombat.com/cdn/shop/files/Red_Tape.png?v=1766095764");' +
      'background-size:100% 100%;background-position:center;background-repeat:no-repeat;' +
      'color:#fff;padding:10px 14px;font-size:15px;font-weight:800;line-height:1;' +
      'white-space:nowrap;pointer-events:none}';
    document.head.appendChild(style);
  }

  function toNumber(el) {
    return parseFloat(el.textContent.replace(/[^0-9.]/g, ''));
  }

  function renderDiscountBadges(root) {
    root = root || document;
    injectBadgeStyles();

    Array.prototype.forEach.call(
      root.querySelectorAll('.custom-discount-badge'),
      function (el) { el.remove(); }
    );

    // PASS 1 - read only. No DOM writes here, so nothing forces a reflow
    // between measurements.
    var pending = [];
    Array.prototype.forEach.call(root.querySelectorAll('.card-wrapper'), function (card) {
      var originalEl = card.querySelector('.price__sale .price-item--regular');
      var saleEl = card.querySelector('.price-item--sale');
      if (!originalEl || !saleEl) return;

      var original = toNumber(originalEl);
      var sale = toNumber(saleEl);
      if (!original || !sale || original <= sale) return;

      var inner = card.querySelector('.card__inner');
      if (!inner) return;

      pending.push({
        inner: inner,
        percent: Math.round(((original - sale) / original) * 100)
      });
    });

    if (!pending.length) return;

    // PASS 2 - write only.
    pending.forEach(function (item) {
      var badge = document.createElement('span');
      badge.className = 'custom-discount-badge';
      badge.textContent = item.percent + '% OFF';
      item.inner.style.position = 'relative';
      item.inner.appendChild(badge);
    });

    log('Rendered', pending.length, 'discount badges');
  }

  window.twdRenderDiscountBadges = renderDiscountBadges;

  /* =======================================================================
     Boot
     ======================================================================= */

  function boot() {
    initSortDrawer();
    initSizeGuide();
    bootVariantGallery();

    // Badges are decorative - let first paint happen first.
    idle(function () { renderDiscountBadges(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('load', initWelcomePopup);

  document.addEventListener('shopify:section:load', function (e) {
    initSortDrawer(e.target);
    initialised = false;
    initVariantGallery();
    idle(function () { renderDiscountBadges(e.target); });
  });

  // Sections mounted late by defer-mount.js need badges too.
  document.addEventListener('defer-mount:mounted', function (e) {
    idle(function () { renderDiscountBadges(e.target); });
  });
})();

/* UK Market: Free Shipping Currency Text */
(function () {
  if (!window.location.pathname.startsWith('/en-gb')) return;

  const updateUKShippingText = (root) => {
    if (!root || root.nodeType !== 1 && root.nodeType !== 9) return;

    // Announcement bar
    root.querySelectorAll?.('.announcement-bar__message').forEach((el) => {
      if (el.textContent.includes('$35')) {
        el.innerHTML = el.innerHTML.replace(/\$35/g, '£25');
      }
    });

    // Trust items
    root.querySelectorAll?.('.pss-trust-item').forEach((el) => {
      const span = el.querySelector('span');

      if (span?.textContent.includes('$35')) {
        span.textContent = span.textContent.replace(/\$35/g, '£25');
      }

      const img = el.querySelector('img');

      if (img?.alt.includes('$35')) {
        img.alt = img.alt.replace(/\$35/g, '£25');
      }
    });

    // Signup popup
    root.querySelectorAll?.('p').forEach((el) => {
      if (el.textContent.includes("You're $35 away from FREE SHIPPING!")) {
        el.innerHTML = el.innerHTML.replace(
          "You're $35 away from FREE SHIPPING!",
          "You're £25 away from FREE SHIPPING!"
        );
      }
    });
  };

  // Initial page load
  const init = () => {
    updateUKShippingText(document);

    /*
     * Watch dynamically added content.
     * Only inspect newly added elements instead of repeatedly
     * scanning the entire document.
     */
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;

          // If the added node itself is relevant
          if (
            node.matches?.(
              '.pss-trust-item, .announcement-bar__message'
            )
          ) {
            updateUKShippingText(node.parentElement || node);
          }

          // Or if relevant elements were added inside it
          if (
            node.querySelector?.(
              '.pss-trust-item, .announcement-bar__message, p'
            )
          ) {
            updateUKShippingText(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

(() => {
    const cleanFromText = () => {
      // Targets all variations of price elements
      const selector = '.price-item, .price-item--sale, .price-item--last, .price-item--regular';
      const priceElements = document.querySelectorAll(selector);
      
      priceElements.forEach(el => {
        // Strip 'From' only if present to prevent unnecessary DOM writes
        if (/From/i.test(el.innerHTML)) {
          el.innerHTML = el.innerHTML.replace(/From\s*/i, '');
        }
      });
    };

    // Run immediately and on DOM load
    cleanFromText();
    document.addEventListener('DOMContentLoaded', cleanFromText);

    // Continuous check for dynamic theme/AJAX updates
    const observer = new MutationObserver(() => cleanFromText());
    observer.observe(document.body, { childList: true, subtree: true });
  })();
  